//* Libraries imports
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/app/store";

//* Types imports
import type { FilledFormOutputDTO } from "@features/filledForm/types";
import type {
  PhaseFormOutputDTO,
  PhaseFormFieldData,
} from "@features/phaseForm/types";

//* Service imports
import { fetchPhaseForms } from "@features/phaseForm/phaseFormSlice";

function extractFieldKeys(ff?: FilledFormOutputDTO | null): string[] {
  if (!ff || !ff.data) return [];
  const d = ff.data as any;
  if (Array.isArray(d)) {
    const keys = d
      .map((item: any) => item?.fieldKey || item?.field?.key || item?.field)
      .filter((k: any) => typeof k === "string");
    return Array.from(new Set(keys));
  }
  if (typeof d === "object") {
    return Object.keys(d);
  }
  return [];
}

function extractPhaseFormFieldKeys(pf?: PhaseFormOutputDTO | null): string[] {
  if (!pf || !pf.formFields) return [];
  const names = (pf.formFields || [])
    .map((f) => f?.name)
    .filter((n): n is string => typeof n === "string" && n.trim() !== "");
  return Array.from(new Set(names));
}

function extractPhaseFormFields(
  pf?: PhaseFormOutputDTO | null
): PhaseFormFieldData[] {
  if (!pf || !pf.formFields) return [];
  return (pf.formFields || []).filter(
    (f) => f?.name && typeof f.name === "string" && f.name.trim() !== ""
  ) as PhaseFormFieldData[];
}

export function useFilledFormFieldKeys(filledFormId?: string | null) {
  const dispatch = useDispatch();
  const state = useSelector((s: RootState) => s.filledForm as any);
  const itemsState = state.items;
  const currentItem = state.currentItem as FilledFormOutputDTO | null;
  const currentIdFallback = state.currentFilledFormId as string | null;
  const phaseState = useSelector((s: RootState) => s.phaseForm);
  const phaseItems = phaseState.items as PhaseFormOutputDTO[];
  const currentPhase = phaseState.currentItem as PhaseFormOutputDTO | null;

  const items = Array.isArray(itemsState)
    ? itemsState
    : (itemsState as any)?.data || [];

  const effectiveId = filledFormId || currentIdFallback || null;

  const current = useMemo(() => {
    if (!effectiveId) return currentItem || null;
    const found = items.find((i: any) => i._id === effectiveId) || null;
    return found || currentItem || null;
  }, [items, effectiveId, currentItem]);

  useEffect(() => {
    if (!phaseItems || phaseItems.length === 0) {
      dispatch(fetchPhaseForms() as any);
    }
  }, [dispatch, phaseItems?.length]);

  const fallbackPhase = useMemo(() => {
    const byCurrentId = current?.phaseForm
      ? phaseItems.find((pf) => pf._id === current?.phaseForm) || null
      : null;
    return byCurrentId || currentPhase || phaseItems[0] || null;
  }, [phaseItems, current?.phaseForm, currentPhase]);

  const fieldKeys = useMemo(() => {
    const fromFilled = extractFieldKeys(current);
    if (fromFilled && fromFilled.length > 0) return fromFilled;
    const fromPhase = extractPhaseFormFieldKeys(fallbackPhase);
    return fromPhase;
  }, [current, fallbackPhase]);

  return { fieldKeys };
}

export function usePhaseFormFieldKeys(phaseFormId?: string | null) {
  const dispatch = useDispatch();
  const phaseState = useSelector((s: RootState) => s.phaseForm);
  const phaseItems = phaseState.items as PhaseFormOutputDTO[];
  const currentPhase = phaseState.currentItem as PhaseFormOutputDTO | null;

  useEffect(() => {
    if (!phaseItems || phaseItems.length === 0) {
      dispatch(fetchPhaseForms() as any);
    }
  }, [dispatch, phaseItems?.length]);

  const selectedPhase = useMemo(() => {
    if (phaseFormId) {
      return phaseItems.find((pf) => pf._id === phaseFormId) || null;
    }
    return null;
  }, [phaseFormId, phaseItems, currentPhase]);

  const fieldKeys = useMemo(
    () => extractPhaseFormFieldKeys(selectedPhase),
    [selectedPhase]
  );

  const fields = useMemo(
    () => extractPhaseFormFields(selectedPhase),
    [selectedPhase]
  );

  return { fieldKeys, fields };
}
